/**
 * Map progression: unlock fields by completing adjacent ones.
 * Domain-only; no I/O and no campaign-content dependency.
 */

export interface MapProgress {
  unlockedFieldIds: Set<string>;
  completedFieldIds: Set<string>;
  stars: Map<string, number>;
  seenStoryBeats: Set<string>;
}

export type AdjacentFieldIdsFn = (fieldId: string) => readonly string[];

export function createInitialMapProgress(startingFieldId: string): MapProgress {
  return {
    unlockedFieldIds: new Set([startingFieldId]),
    completedFieldIds: new Set(),
    stars: new Map(),
    seenStoryBeats: new Set(),
  };
}

/** Mark field complete, record stars, unlock 4-directional neighbors. */
export function completeField(
  progress: MapProgress,
  fieldId: string,
  getAdjacentFieldIds: AdjacentFieldIdsFn,
  stars: number = 1
): void {
  progress.completedFieldIds.add(fieldId);
  progress.stars.set(fieldId, Math.max(stars, progress.stars.get(fieldId) ?? 0));
  for (const adj of getAdjacentFieldIds(fieldId)) {
    progress.unlockedFieldIds.add(adj);
  }
}

export function isFieldUnlocked(progress: MapProgress, fieldId: string): boolean {
  return progress.unlockedFieldIds.has(fieldId);
}

export function isFieldCompleted(progress: MapProgress, fieldId: string): boolean {
  return progress.completedFieldIds.has(fieldId);
}
